import { isTextMessageAccepted } from '../../entities/message'
// Chat screen — ports messaging.js (send/history/start) + ui.js (render/typing/
// scroll) + the connection wiring. WebSocket is primary (useChatConnection);
// HTTP /message is the fallback when the socket isn't open. 402 / show_pricing
// route to the paywall. Practices hand off a queued command via chatStore.

import { useCallback, useEffect, useRef } from 'react'
import { useChatDraft } from '../../features/chat-draft'
import { useNavigate } from 'react-router-dom'
import { useMessagingStore } from '../../store/messagingStore'
import { useChatStore } from '../../store/chatStore'
import { useAuthStore } from '../../store/authStore'
import { useProfileStore } from '../../store/profileStore'
import { useChatConnection } from '../../hooks/useChatConnection'
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder'
import { chatApi, normalizeServerMessage } from '../../api/chat'
import { ApiError } from '../../api/client'
import { toast } from '../../store/toastStore'
import * as metrica from '../../lib/metrica'
import { ensureClientIdBackfilled } from '../../lib/ymUid'
import Message from '../../components/chat/Message'
import TypingIndicator from '../../components/chat/TypingIndicator'
import styles from './Chat.module.css'
import { analytics } from '../../lib/analytics'

const STATUS_LABEL = {
  connected: 'Подключено',
  connecting: 'Подключение...',
  offline: 'Оффлайн',
  failed: 'Нет связи',
}

// Volume-meter intensity bucket (original .volume-bar.low/.mid/.high, voice.js:300-306).
function volBucketClass(v: number) {
  if (v < 0.15) return styles.volLow
  if (v < 0.5) return styles.volMid
  return styles.volHigh
}

export default function Chat() {
  const navigate = useNavigate()
  const accessToken = useAuthStore((s) => s.accessToken)
  const displayName = useAuthStore((s) => s.displayName)

  const messages = useMessagingStore((s) => s.messages)
  const isLoading = useMessagingStore((s) => s.isLoading)
  const typing = useMessagingStore((s) => s.typing)
  const setMessages = useMessagingStore((s) => s.setMessages)
  const addMessage = useMessagingStore((s) => s.addMessage)
  const setLoading = useMessagingStore((s) => s.setLoading)
  const setTyping = useMessagingStore((s) => s.setTyping)
  const clear = useMessagingStore((s) => s.clear)
  const queuePending = useMessagingStore((s) => s.queuePending)

  const stats = useProfileStore((s) => s.stats)
  const loadStats = useProfileStore((s) => s.loadStats)

  const [input, setInput] = useChatDraft()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const stickToBottomRef = useRef(true) // start pinned so the first load lands at the bottom
  const autoSentRef = useRef(false)

  // Token usage bar (token-based plans) + proactive paywall re-check. Forced
  // (not cached) so a returning user (e.g. after logging back in, when the
  // local paywall bubble was wiped for privacy — see useAuth.js logout())
  // sees the paywall again immediately if access is still denied, instead of
  // only after sending a throwaway message and getting a fresh 402.
  useEffect(() => {
    loadStats(true).then(() => {
      if (!useProfileStore.getState().isAccessDenied()) return
      // Route through addLimitNotice so a depleted token-pack ("Сессия") user
      // gets the tailored depletion paywall immediately — addPaywall() alone
      // would show the generic "Пробный период завершён" intro, which the
      // subsequent 402 would then replace, briefly flashing the wrong copy.
      // addLimitNotice forwards token_based denials to addPaywall(depleted).
      useMessagingStore.getState().addLimitNotice(null)
    })
  }, [loadStats])

  const tokenBar =
    stats?.plan_type === 'token_based' && stats?.tokens_remaining != null
      ? {
          remaining: stats.tokens_remaining,
          percent: Math.round((stats.tokens_remaining / (stats.token_limit || 8000)) * 100),
        }
      : null

  const { status, send } = useChatConnection({ enabled: Boolean(accessToken) })
  // Access denied → inline paywall in the transcript (parent feat/inline-paywall).
  const showPaywall = useCallback(() => useMessagingStore.getState().addPaywall(), [])
  const voice = useVoiceRecorder({ onPaywall: showPaywall })

  // Load fresh history once on mount (falls back to persisted messages on error).
  useEffect(() => {
    let cancelled = false
    let startTimer: ReturnType<typeof setTimeout> | null = null
    chatApi
      .history(50)
      .then((data) => {
        if (cancelled) return
        // Fill-only-empty: history rows carry no engine metadata (bd-78u), so
        // replacing a freshly populated store (the just-onboarded greeting)
        // would wipe its scale keyboard (redmine 22995 item 1). The persisted
        // local store stays authoritative for the session; history fills a
        // cold start (new device / after logout cleared it).
        if (data?.messages?.length && useMessagingStore.getState().messages.length === 0) {
          setMessages(data.messages.map((m) => normalizeServerMessage(m, 'assistant')))
          return
        }
        // Empty server history + nothing persisted → guarantee an opening
        // greeting even if the WebSocket never opens (original called /start in
        // HTTP/polling mode, messaging.js:14-55). Give the WS `welcome` a moment
        // first so we never show two greetings.
        if (useMessagingStore.getState().messages.length > 0) return
        startTimer = setTimeout(async () => {
          if (cancelled || useMessagingStore.getState().messages.length > 0) return
          try {
            const msg = await chatApi.start()
            const first = Array.isArray(msg) ? msg[0] : msg
            const content = first?.content
            if (content && useMessagingStore.getState().messages.length === 0) {
              addMessage('assistant', content, { metadata: first?.metadata })
            }
          } catch {
            /* no greeting available — keep the welcome placeholder */
          }
        }, 2500)
      })
      .catch(() => {
        /* keep persisted localStorage messages */
      })
    return () => {
      cancelled = true
      if (startTimer) clearTimeout(startTimer)
    }
  }, [setMessages, addMessage])

  // Autoscroll to bottom when messages or typing change.
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])
  useEffect(() => {
    scrollToBottom()
  }, [messages, typing, scrollToBottom])

  // Keep the transcript pinned to the bottom across LATE size changes, but only
  // while the user is AT the bottom (never yank them up while reading history):
  //   - the CONTAINER (.scroll) resizes on iOS 100dvh resettle / keyboard / token
  //     bar — observed via scrollRef.
  //   - the CONTENT (.scrollInner) grows when practice videos/audio load their
  //     metadata and resize (often several, after the one-shot initial scroll) —
  //     the container observer can't see this, so we observe the inner too.
  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const NEAR_BOTTOM_PX = 80
    const onScroll = () => {
      stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) el.scrollTop = el.scrollHeight
    })
    ro.observe(el)
    if (contentRef.current) ro.observe(contentRef.current)
    return () => {
      el.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [])

  // Late-loading media (video metadata/poster, images) grows the transcript AFTER
  // the initial scroll — re-pin to the bottom, but only if the user is already
  // there (don't yank someone who scrolled up to watch). Content growing above the
  // viewport doesn't fire a scroll event, so stickToBottomRef stays true while the
  // user was at the bottom. iOS video with preload=metadata fires loadedmetadata
  // (not always loadeddata), so Message wires both to this.
  const onMediaLoad = useCallback(() => {
    const el = scrollRef.current
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight
  }, [])

  // Refresh the token balance once a reply completes (typing true→false), so the
  // token bar reflects consumed tokens (original refreshed after each send,
  // messaging.js:146). Covers both the WS and HTTP paths.
  const prevTyping = useRef(false)
  const isTokenPlan = Boolean(tokenBar)
  useEffect(() => {
    if (prevTyping.current && !typing && isTokenPlan) loadStats(true)
    prevTyping.current = typing
  }, [typing, isTokenPlan, loadStats])

  // Core send — mirrors messaging.js sendMessage. `displayText` lets Practices
  // show a friendly label while sending the real command.
  const handleSend = useCallback(
    async (rawContent: string, displayText: string | null = null) => {
      const content = rawContent.trim()
      if (!content || useMessagingStore.getState().isLoading) return

      if (!navigator.onLine) {
        toast('Нет подключения к интернету. Сообщение будет отправлено после восстановления связи.', 'error')
        queuePending(content)
        return
      }

      addMessage('user', displayText || content)
      setInput('')
      setTyping(true)
      setLoading(true)

      // By the first chat send the Metrica tag has had time to initialize —
      // backfill the ClientID the auth-moment read likely missed (bd-ttr).
      // Idempotent per session; fire-and-forget, never blocks the send.
      ensureClientIdBackfilled()

      // Prefer the open socket; the hook's onmessage finishes the turn.
      if (send(content)) return

      // HTTP fallback.
      try {
        const responses = await chatApi.send(content)
        if (isTextMessageAccepted(responses)) analytics.track('messageAccepted')
        setTyping(false)
        metrica.reachGoal('message_sent')
        const { addPaywall, addLimitNotice } = useMessagingStore.getState()
        for (const msg of responses) {
          if (msg.metadata?.action === 'show_pricing') addPaywall()
          else if (msg.metadata?.action === 'show_limit_notice') addLimitNotice(msg.metadata.reset_at)
          else if (msg.metadata?.action === 'clear_chat') clear()
          else addMessage('assistant', msg.content, { contentType: msg.content_type || 'text', mediaUrl: msg.media_url || null, metadata: msg.metadata ?? null })
        }
      } catch (err) {
        setTyping(false)
        if (err instanceof ApiError && err.status === 402) {
          // Subscribed users over quota get a friendly notice; everyone else the paywall.
          const detail = err.body?.detail
          if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.reason === 'subscription_limit')
            useMessagingStore.getState().addLimitNotice(detail.reset_at)
          else useMessagingStore.getState().addPaywall()
        } else {
          toast((err instanceof Error && err.message) || 'Не удалось отправить сообщение', 'error')
        }
      } finally {
        setLoading(false)
      }
    },
    [addMessage, clear, queuePending, send, setInput, setLoading, setTyping]
  )

  // Practices → chat handoff: send the queued command once.
  useEffect(() => {
    if (autoSentRef.current) return
    const { pendingInput, pendingDisplay, consumeInput } = useChatStore.getState()
    if (pendingInput) {
      autoSentRef.current = true
      consumeInput()
      handleSend(pendingInput, pendingDisplay)
    }
  }, [handleSend])

  const offline = status === 'offline'
  const canSend = !offline && !isLoading && input.trim()

  return (
    <div className={styles.chat}>
      <div className={styles.statusBar}>
        <span className={`${styles.connectionStatus} ${styles[status] || ''}`}>{STATUS_LABEL[status] || ''}</span>
        {displayName && <span className={styles.userName}>{displayName}</span>}
      </div>

      {tokenBar && (
        <div className={styles.tokenProgress}>
          <div className={styles.tokenFill} style={{ width: `${tokenBar.percent}%` }} />
          <span className={styles.tokenText}>{tokenBar.remaining.toLocaleString()} токенов</span>
        </div>
      )}

      <div className={styles.scroll} ref={scrollRef} data-testid="chat-scroll">
        <div className={styles.scrollInner} ref={contentRef}>
          {messages.length === 0 && !typing ? (
            <div className={styles.welcome}>
              <div className={styles.welcomeIcon}>👋</div>
              <h2>Добро пожаловать в Humanteq!</h2>
              <p>Начните разговор с AI-психологом</p>
            </div>
          ) : (
            messages.map((msg, i) => (
              <Message key={i} msg={msg} index={i} onInsightCta={() => navigate('/psychologist')} onMediaLoad={onMediaLoad} />
            ))
          )}
          {typing && <TypingIndicator />}
        </div>
      </div>

      <footer className={styles.inputContainer}>
        {voice.isRecording && (
          <div className={styles.recordingIndicator}>
            <span className={styles.recDot} />
            <span className={styles.recTime}>
              {Math.floor(voice.elapsed / 60)}:{String(voice.elapsed % 60).padStart(2, '0')}
            </span>
            <div className={`${styles.volumeMeter} ${voice.speaking ? styles.speech : ''}`}>
              <div
                className={`${styles.volumeBar} ${volBucketClass(voice.volume)}`}
                style={{ width: `${Math.round(voice.volume * 100)}%` }}
              />
            </div>
            <span className={styles.recStatus}>{voice.speaking ? 'Говорите...' : 'Слушаю...'}</span>
          </div>
        )}

        <form
          className={styles.inputWrapper}
          onSubmit={(e) => {
            e.preventDefault()
            if (canSend) handleSend(input)
          }}
        >
          <input
            type="text"
            className={styles.messageInput}
            placeholder={offline ? 'Нет подключения к интернету...' : 'Напишите сообщение...'}
            value={input}
            disabled={offline}
            autoComplete="off"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (canSend) handleSend(input)
              }
            }}
          />
          <button className={styles.sendBtn} type="submit" disabled={!canSend} aria-label="Отправить">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
          <button
            className={`${styles.voiceBtn} ${voice.isRecording ? styles.recording : ''}`}
            type="button"
            onClick={voice.toggle}
            disabled={offline || isLoading}
            aria-label="Записать голосовое сообщение"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </button>
        </form>
      </footer>
    </div>
  )
}
